import Icon from 'core/components/icons/Icon'
import { useCallback, useMemo } from 'react'
import FundingMethodItem from 'core/widgets/funding-modal/funding-methods/FundingMethodItem'
import Button from 'core/components/buttons/Button'
import { useFormContext } from 'react-hook-form'
import { FundingModalFormValues } from 'core/widgets/funding-modal/FundingModal'
import useIndexedPayPeriods from 'core/widgets/funding-modal/useIndexedPayPeriods'
import {
  getPayPeriodsTotal,
  getSelectedPayPeriodsFromIds
} from 'utils/Functions'
import numeral from 'numeral'
import AchDebitMethod from 'core/widgets/funding-modal/funding-methods/AchDebitMethod'
import useAmountBrief from 'core/widgets/funding-modal/useAmountBrief'
import useIsFboWallet from 'core/hooks/wallet/useIsFboWallet'

const FundingMethods = () => {
  const isFbo = useIsFboWallet()
  const { watch, setValue } = useFormContext<FundingModalFormValues>()
  const { indexedPayPeriods, allPayPeriodsTotal } = useIndexedPayPeriods()
  const [fundingMethod, amountType, selectedPayPeriodIds, customAmount] = watch(
    ['funding_method', 'amountType', 'selected_pay_periods', 'customAmount']
  )
  const amountBrief = useAmountBrief()

  const selectedPayPeriods = useMemo(
    () => getSelectedPayPeriodsFromIds(indexedPayPeriods, selectedPayPeriodIds),
    [selectedPayPeriodIds, indexedPayPeriods]
  )

  const finalAmount = useMemo(() => {
    if (amountType === 'selected-pay-periods') {
      return getPayPeriodsTotal(selectedPayPeriods)
    }
    if (amountType === 'all-pay-periods') {
      return allPayPeriodsTotal
    }
    return customAmount
  }, [selectedPayPeriods, allPayPeriodsTotal, customAmount, amountType])

  const changeFundingMethod = useCallback(
    (fundingMethod: FundingModalFormValues['funding_method']) => () => {
      if (fundingMethod === undefined) return
      setValue('funding_method', fundingMethod)
      if (amountType === 'other-amount') {
        setValue('auto_pay', false)
      }
      if (fundingMethod !== 'ach-debit') {
        setValue('view', fundingMethod)
      }
    },
    [setValue, amountType]
  )

  return (
    <>
      <div className="rounded-md bg-dark-8 px-5 pt-10 pb-12 flex justify-between items-center mb-5">
        <div className="heading-2 text-dark-4">Amount</div>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-5">
            <div className="text-[36px] font-bold">
              {numeral(finalAmount).format('$ 0,0.00')}
            </div>
            <Icon
              name="edit-outline"
              color="text-primary"
              size={20}
              className="cursor-pointer"
              onClick={() => {
                setValue('view', 'edit-amount')
              }}
            />
          </div>
          <div className="subtitle-2 text-dark-4">{amountBrief}</div>
        </div>
      </div>
      <div className={fundingMethod ? '' : 'mb-20'}>
        <div className="mb-3 subtitle-1 font-semibold">Funding Method</div>
        <div className="flex flex-col gap-4">
          {isFbo === false && (
            <AchDebitMethod
              fundingMethod={fundingMethod}
              onMfaAuthenticated={changeFundingMethod('ach-debit')}
            />
          )}
          {fundingMethod !== 'ach-debit' && (
            <>
              <FundingMethodItem
                title="ACH Credit"
                info="On time for July 28th scheduled pay date"
                showCheckIcon={false}
                isSelected={fundingMethod === 'ach-credit'}
                onClick={changeFundingMethod('ach-credit')}
              />
              <FundingMethodItem
                title="Wire transfer"
                info="On time for July 28th scheduled pay date"
                showCheckIcon={true}
                isSelected={fundingMethod === 'wire-transfer'}
                onClick={changeFundingMethod('wire-transfer')}
              />
            </>
          )}
        </div>
      </div>
      {fundingMethod === undefined && (
        <Button variant="primary" className="w-full h-[48px]" disabled={true}>
          Initiate funding
        </Button>
      )}
    </>
  )
}

export default FundingMethods
